import React, { useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import unsplash from "../api/unsplash";
import { consulenzaAuth } from "./useConsulenzaData";
import { decimalFieldSx } from "./consulenzaFields";
const euro = (n) =>
  Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
function fotografiaPerAnno(fotografie = [], anno) {
  const f = fotografie.find((x) => Number(x.Anno) === Number(anno));
  if (f) return { ...f, Confermata: true, AnnoOrigine: Number(f.Anno) };
  const precedente = fotografie
    .filter((x) => Number(x.Anno) < Number(anno))
    .sort((a, b) => Number(b.Anno) - Number(a.Anno))[0];
  return precedente
    ? {
        ...precedente,
        Anno: Number(anno),
        Confermata: false,
        AnnoOrigine: Number(precedente.Anno),
      }
    : null;
}
export default function SorvegliatiAnnuali({
  clienteId,
  contratto,
  anno,
  onSaved,
  numeroAttuale,
}) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const foto = fotografiaPerAnno(contratto.FotografieAnnuali, anno);
  const attivoNellAnno =
    contratto.DataInizio <= `${anno}-12-31` &&
    (!contratto.DataFine || contratto.DataFine >= `${anno}-01-01`);
  const scegliAnno = (a) => {
    const f = fotografiaPerAnno(contratto.FotografieAnnuali, a);
    setForm({
      Anno: a,
      NumeroSorvegliati: f?.NumeroSorvegliati ?? numeroAttuale ?? "",
      TariffaSorvegliato: f?.TariffaSorvegliato ?? "",
    });
    setError("");
  };
  const totale =
    form &&
    form.NumeroSorvegliati !== "" &&
    form.TariffaSorvegliato !== "" &&
    Number.isFinite(
      Number(form.NumeroSorvegliati) * Number(form.TariffaSorvegliato),
    )
      ? Number(form.NumeroSorvegliati) * Number(form.TariffaSorvegliato)
      : null;
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await unsplash.post(
        "contrattiConsulenza/sorvegliati?settore=medicina",
        {
          cliente: clienteId,
          contratto: contratto.ID,
          fotografia: form,
        },
        consulenzaAuth(),
      );
      setForm(null);
      await onSaved();
    } catch (e) {
      setError(
        e.response?.data?.message ||
          "Impossibile salvare la fotografia annuale.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle1">
          Sorvegliati annui — contratto {contratto.ID} · decorrenza{" "}
          {moment(contratto.DataInizio).format("DD/MM/YYYY")}
        </Typography>
        {foto ? (
          <Stack spacing={1}>
            {!foto.Confermata && attivoNellAnno && (
              <Alert severity="warning">
                Fotografia {anno} da confermare: previsione automatica dai dati
                confermati del {foto.AnnoOrigine}. Verifica il numero e la
                tariffa a inizio anno, poi salva la fotografia anche se i valori
                restano uguali.
              </Alert>
            )}
            <Typography>
              {foto.Confermata ? "Fotografia confermata" : "Previsione"} {anno}:{" "}
              {foto.NumeroSorvegliati} sorvegliati ×{" "}
              {euro(foto.TariffaSorvegliato)} annui ={" "}
              {euro(
                Number(foto.NumeroSorvegliati) *
                  Number(foto.TariffaSorvegliato),
              )}
              .
            </Typography>
          </Stack>
        ) : (
          attivoNellAnno && (
            <Alert severity="warning">
              Fotografia {anno} da aggiornare: inserisci numero e tariffa
              annuali. Le quote che richiedono questo dato restano escluse dal
              totale.
            </Alert>
          )
        )}
        {foto?.Confermata && foto.AggiornatoIl && (
          <Typography variant="caption" color="text.secondary">
            Ultimo aggiornamento: {foto.AggiornatoIl} · {foto.AggiornatoDa}
          </Typography>
        )}
        <Button
          variant="outlined"
          sx={{ alignSelf: "flex-start" }}
          onClick={() =>
            scegliAnno(
              Math.max(Number(anno), Number(contratto.DataInizio.slice(0, 4))),
            )
          }
        >
          Gestisci fotografia annuale
        </Button>
      </Stack>
      <Dialog
        open={!!form}
        onClose={() => !busy && setForm(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Fotografia annuale sorvegliati — contratto {contratto.ID}
        </DialogTitle>
        <DialogContent>
          {form && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">
                Il numero è la fotografia dell’anno, non viene ricalcolato dalle
                anagrafiche lavoratori. Per gli anni successivi Koala propone
                numero e tariffa dell’ultima fotografia confermata; a inizio
                anno verifica i valori e salva per confermarli. La tariffa è
                annua per persona.
              </Alert>
              {(() => {
                const proposta = fotografiaPerAnno(
                  contratto.FotografieAnnuali,
                  form.Anno,
                );
                return proposta && !proposta.Confermata ? (
                  <Alert severity="warning">
                    Valori proposti dal {proposta.AnnoOrigine}: il salvataggio
                    conferma la fotografia {form.Anno}.
                  </Alert>
                ) : null;
              })()}
              {numeroAttuale != null && (
                <Alert severity="info">
                  Koala rileva oggi {numeroAttuale} lavoratori attivi nella
                  medicina del cliente, esclusi quelli fuori KPI. Verifica e
                  inserisci il numero corretto per l’anno scelto; il dato
                  attuale non ricostruisce gli anni passati.
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={() =>
                      setForm({ ...form, NumeroSorvegliati: numeroAttuale })
                    }
                  >
                    Usa numero attuale
                  </Button>
                </Alert>
              )}
              {!!contratto.FotografieAnnuali?.length && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {contratto.FotografieAnnuali.map((f) => (
                    <Button
                      key={f.Anno}
                      size="small"
                      disabled={busy}
                      onClick={() => scegliAnno(f.Anno)}
                    >
                      Anno {f.Anno}
                    </Button>
                  ))}
                </Box>
              )}
              <TextField
                label="Anno fotografia"
                type="number"
                value={form.Anno}
                disabled={busy}
                inputProps={{ min: 1900, max: 2200, step: 1 }}
                onChange={(e) => scegliAnno(e.target.value)}
              />
              <TextField
                label="Numero sorvegliati"
                type="number"
                value={form.NumeroSorvegliati}
                disabled={busy}
                inputProps={{ min: 0, max: 1000000, step: 1 }}
                onChange={(e) =>
                  setForm({ ...form, NumeroSorvegliati: e.target.value })
                }
              />
              <TextField
                label="Tariffa annua per sorvegliato (€)"
                type="number"
                value={form.TariffaSorvegliato}
                disabled={busy}
                sx={decimalFieldSx}
                inputProps={{ min: 0, step: "0.01" }}
                onChange={(e) =>
                  setForm({ ...form, TariffaSorvegliato: e.target.value })
                }
              />
              <Typography>
                Canone annuo risultante:{" "}
                {totale === null ? "Inserisci numero e tariffa" : euro(totale)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Il canone è ripartito secondo la periodicità del contratto. I
                periodi a cavallo di gennaio usano i dati dei rispettivi anni;
                l’eventuale conguaglio dei centesimi è a dicembre. Modificando
                una fotografia aggiorni anche i prospetti passati di quell’anno.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setForm(null)}>
            Annulla
          </Button>
          <Button disabled={busy} variant="contained" onClick={save}>
            {busy ? "Salvataggio…" : "Salva fotografia"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
